'use client';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ApiAttribute } from '@context/openapi/types';
import InputLabel from '@components/ui/DynamicForm/InputLabel';
import { Box } from '@radix-ui/themes';
import { createHighlighter, Highlighter } from 'shikiv2';
import { cn } from '@/lib/utils';
interface JSONInputProps {
  field: ApiAttribute;
  onChange: (field: ApiAttribute, value: string) => void;
}

export const JSONInput: React.FC<JSONInputProps> = ({ field, onChange }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [jsonText, setJsonText] = useState<string>(
    field.value?.toString() || ''
  );
  const [expanded, setExpanded] = useState(false);
  const [highlightedHtml, setHighlightedHtml] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [highlighter, setHighlighter] = useState<Highlighter | null>(null);

  // Sync jsonText with field.value when it changes externally
  useEffect(() => {
    const fieldValue = field.value?.toString() || '';
    if (fieldValue !== jsonText) {
      setJsonText(fieldValue);
    }
  }, [field.value]);

  useEffect(() => {
    createHighlighter({
      themes: ['min-light'],
      langs: ['json'],
    }).then(setHighlighter);
  }, []);

  useEffect(() => {
    if (!highlighter) return;
    const highlighted = highlighter.codeToHtml(jsonText, {
      lang: 'json',
      theme: 'min-light',
    });
    setHighlightedHtml(highlighted);
  }, [jsonText, highlighter]);

  const expand = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setJsonText('');
    onChange(field, '');
  };

  const handleFocus = (hovered: boolean) => {
    setIsFocused(hovered);
  };

  return (
    <Box
      key={field.name}
      className={`bg-[#d3e4e6] 
        border-b border-solid border-form-border
        border-l border-r`}
    >
      <div
        className={`pl-2 flex gap-2 justify-between w-full hover:bg-[#d3e4e6] group ${isFocused && 'bg-[#d3e4e6]'}`}
        onClick={expand}
      >
        <InputLabel field={field} />
        <div className="ml-auto flex-1 cursor-pointer">
          {!expanded && (
            <button
              className="text-xs py-1 pl-1 text-neutral-400 opacity-100 focus-visible:outline-shade-aqua-300 focus-visible:px-2"
              onFocus={() => handleFocus(true)}
              onBlur={() => handleFocus(false)}
            >
              Click to expand
            </button>
          )}
          {expanded && (
            <button
              className="text-xs  float-right py-1 mr-2 focus-visible:outline-shade-aqua-300 focus-visible:px-2"
              onClick={clear}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="relative w-full">
          {/* Shiki Highlighted JSON (Background Layer) */}
          <pre
            className="mx-2 my-1 p-2 absolute bg-white text-sm inset-0 rounded font-mono overflow-auto pointer-events-none whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
          />

          {/* Transparent Textarea (User Input) */}
          <textarea
            ref={textareaRef}
            value={jsonText}
            onChange={(e) => {
              const newValue = e.target.value;
              setJsonText(newValue);
              onChange(field, newValue);
            }}
            className={cn(
              'mx-2 my-1 p-2 relative w-full text-sm caret-black h-40 font-mono bg-transparent text-transparent rounded-md resize-none focus:outline-none'
            )}
            data-pendo-mask
            spellCheck={false}
          />
        </div>
      )}
    </Box>
  );
};

export default JSONInput;
